import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface TalentRecommendation {
  talent_id: string;
  profile_id: string;
  name: string;
  sport: string;
  photo?: string;
  tier: string;
  total_followers: number;
  instagram_handle?: string;
  competition_level?: string;
  team_name?: string;
  avatar_url?: string;
  match_score: number;
  reasoning: string;
  key_strengths: string[];
}

export const useAITalentRecommendations = (dealId: string) => {
  const [recommendations, setRecommendations] = useState<TalentRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchRecommendations = useCallback(async () => {
    if (!dealId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Fetching AI recommendations for deal:', dealId);
      
      const { data, error: functionError } = await supabase.functions.invoke('recommend-talent', {
        body: { dealId }
      });

      if (functionError) {
        console.error('Error calling recommend-talent function:', functionError);
        throw functionError;
      }

      if (data.error) {
        throw new Error(data.error);
      }

      console.log('Received recommendations:', data.recommendations?.length || 0);
      setRecommendations(data.recommendations || []);

    } catch (err: any) {
      console.error('Error fetching recommendations:', err);
      const errorMessage = err.message || 'Failed to fetch AI recommendations';
      setError(errorMessage);
      
      toast({
        title: "Could not load AI recommendations",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [dealId, toast]);

  const refreshRecommendations = useCallback(() => {
    setRecommendations([]);
    fetchRecommendations();
  }, [fetchRecommendations]);

  const pairTalentWithDeal = useCallback(async (talentProfileId: string) => {
    try {
      console.log('Pairing talent with deal:', { dealId, talentProfileId });
      
      const { error } = await supabase
        .from('deal_applications')
        .insert({
          deal_id: dealId,
          talent_profile_id: talentProfileId,
          status: 'pending',
          initiated_by: 'brand',
        });

      if (error) {
        if (error.code === '23505') {
          throw new Error('This talent has already been paired with this deal');
        }
        throw error;
      }

      return { success: true };
    } catch (err: any) {
      console.error('Error pairing talent:', err);
      throw err;
    }
  }, [dealId]);

  return {
    recommendations,
    loading,
    error,
    fetchRecommendations,
    refreshRecommendations,
    pairTalentWithDeal
  };
};
